import React, { useState } from 'react';
import { Link } from 'react-router';
import { importLscCsv, LscApiError } from '../api';
import type { LscCsvImportResult } from '../types';
import LscMutationErrorAlert from './LscMutationErrorAlert';
import { parseLscMutationError, type ParsedLscMutationError } from '../entitlementBillingErrors';
import { lscPath } from '../routing';
import {
  IMPORT_SEASON_OPTIONS,
  IMPORT_STAGE_OPTIONS,
  SAMPLE_CSV_PATH,
  NEONSPORTZ_GAMES_SAMPLE_PATH,
  NEONSPORTZ_TEAMS_SAMPLE_PATH,
} from '../importConstants';
import { displaySeasonLabel } from '../seasonDisplay';
import {
  extractParticipantPreview,
  formatCsvImportErrorMessage,
  formatImportIssues,
} from '../importErrorUtils';

interface CsvImportPanelProps {
  leagueId: string;
  onImportSuccess?: () => void;
}

const CsvImportPanel: React.FC<CsvImportPanelProps> = ({ leagueId, onImportSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [dryRun, setDryRun] = useState(true);
  const [apply, setApply] = useState(false);
  const [seasonLabel, setSeasonLabel] = useState('');
  const [currentStage, setCurrentStage] = useState('');
  const [gameKey, setGameKey] = useState('');
  const [edition, setEdition] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ParsedLscMutationError | null>(null);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const [errorIssues, setErrorIssues] = useState<string[]>([]);
  const [result, setResult] = useState<LscCsvImportResult | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError({
        message: 'Choose a CSV file to import.',
        showBillingLink: false,
        showPricingLink: false,
      });
      setErrorDetails(null);
      setErrorIssues([]);
      return;
    }

    setLoading(true);
    setError(null);
    setErrorDetails(null);
    setErrorIssues([]);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('dry_run', dryRun ? 'true' : 'false');
    formData.append('apply', apply ? 'true' : 'false');
    if (seasonLabel) formData.append('season_label', seasonLabel);
    if (currentStage) formData.append('current_stage', currentStage);
    if (gameKey) formData.append('game_key', gameKey);
    if (edition.trim()) formData.append('edition', edition.trim());

    try {
      const data = await importLscCsv(leagueId, formData);
      setResult(data);
      if (apply && !dryRun) {
        onImportSuccess?.();
      }
    } catch (err) {
      if (err instanceof LscApiError) {
        const parsed = parseLscMutationError(err, 'Import failed.');
        setError(parsed);
        setErrorDetails(formatCsvImportErrorMessage(err.message, err.details));
        setErrorIssues(formatImportIssues(err.issues));
      } else {
        setError({
          message: 'Import failed.',
          showBillingLink: false,
          showPricingLink: false,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const participantPreview = extractParticipantPreview(result?.snapshot_preview);

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="card space-y-4" data-testid="csv-import-panel">
        <div>
          <h2 className="text-lg font-semibold text-white mb-1">Schedule import</h2>
          <p className="text-sm text-secondary-400">
            Upload a Stream Center schedule CSV or a NeonSportz games/teams export. NeonSportz
            username, in-game identity, and Twitch login are stored separately — Stream Center does
            not infer Twitch from NeonSportz fields.{' '}
            <a
              href={SAMPLE_CSV_PATH}
              className="text-primary-400 hover:text-primary-300"
              download
              data-testid="sample-csv-link"
            >
              Generic sample
            </a>
            {' · '}
            <a
              href={NEONSPORTZ_GAMES_SAMPLE_PATH}
              className="text-primary-400 hover:text-primary-300"
              download
            >
              NeonSportz games sample
            </a>
            {' · '}
            <a
              href={NEONSPORTZ_TEAMS_SAMPLE_PATH}
              className="text-primary-400 hover:text-primary-300"
              download
            >
              NeonSportz teams sample
            </a>
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-secondary-300 mb-1" htmlFor="csv-file">
            CSV file
          </label>
          <input
            id="csv-file"
            type="file"
            accept=".csv,text/csv"
            className="input-field"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <label className="block text-sm text-secondary-300 mb-1" htmlFor="season-label">
              Season label
            </label>
            <select
              id="season-label"
              className="input-field"
              value={seasonLabel}
              onChange={(e) => setSeasonLabel(e.target.value)}
              data-testid="season-label-select"
            >
              <option value="">Select season (required unless the file has seasonIndex)</option>
              {IMPORT_SEASON_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {displaySeasonLabel(option)}
                </option>
              ))}
            </select>
            <p className="text-xs text-secondary-500 mt-1">
              Required unless your file includes a seasonIndex column.
            </p>
          </div>
          <div>
            <label className="block text-sm text-secondary-300 mb-1" htmlFor="current-stage">
              Current stage
            </label>
            <select
              id="current-stage"
              className="input-field"
              value={currentStage}
              onChange={(e) => setCurrentStage(e.target.value)}
              data-testid="current-stage-select"
            >
              <option value="">Select stage (optional)</option>
              {IMPORT_STAGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-secondary-300 mb-1" htmlFor="game-key">
              Game key (optional)
            </label>
            <input
              id="game-key"
              className="input-field"
              value={gameKey}
              onChange={(e) => setGameKey(e.target.value)}
              placeholder="custom"
            />
          </div>
          <div>
            <label className="block text-sm text-secondary-300 mb-1" htmlFor="csv-edition">
              Edition
            </label>
            <input
              id="csv-edition"
              className="input-field"
              value={edition}
              onChange={(e) => setEdition(e.target.value)}
              placeholder="madden-27"
              data-testid="csv-edition-input"
            />
            <p className="text-xs text-secondary-500 mt-1">
              Required when this league already has an edition. A blank edition is rejected once the league has one.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-2 text-sm text-secondary-300">
            <input
              type="checkbox"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
            />
            Dry run (preview only)
          </label>
          <label className="flex items-center gap-2 text-sm text-secondary-300">
            <input
              type="checkbox"
              checked={apply}
              disabled={dryRun}
              onChange={(e) => setApply(e.target.checked)}
            />
            Apply matches after import
          </label>
        </div>

        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? 'Importing...' : dryRun ? 'Preview import' : 'Import schedule'}
        </button>
      </form>

      {error && (
        <div className="space-y-2" data-testid="csv-import-error">
          <LscMutationErrorAlert error={error} onDismiss={() => setError(null)} />
          {errorDetails && errorDetails !== error.message && (
            <pre className="text-xs text-secondary-400 whitespace-pre-wrap bg-secondary-900/50 p-3 rounded">
              {errorDetails}
            </pre>
          )}
          {errorIssues.length > 0 && (
            <ul className="list-disc list-inside text-sm text-red-200">
              {errorIssues.map((issue) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {result && (
        <div className="card space-y-3">
          <h3 className="font-semibold text-white">
            {result.dry_run ? 'Import preview' : 'Import result'}
          </h3>
          {result.detected_format && (
            <p className="text-sm text-secondary-400">
              Detected format: <span className="text-secondary-200">{result.detected_format}</span>
            </p>
          )}
          {result.match_count != null && (
            <p className="text-sm text-secondary-300">
              Matches: {result.match_count} · Participants: {result.participant_count ?? '—'}
            </p>
          )}
          {result.warnings && result.warnings.length > 0 && (
            <div>
              <p className="text-sm font-medium text-amber-300 mb-1">Warnings</p>
              <ul className="list-disc list-inside text-sm text-secondary-400">
                {formatImportIssues(result.warnings).map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </div>
          )}
          {result.errors && result.errors.length > 0 && (
            <div>
              <p className="text-sm font-medium text-red-300 mb-1">Errors</p>
              <ul className="list-disc list-inside text-sm text-red-200">
                {formatImportIssues(result.errors).map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </div>
          )}
          {participantPreview.length > 0 && (
            <div>
              <p className="text-sm font-medium text-secondary-200 mb-2">Team identity preview</p>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-left text-secondary-300">
                  <thead>
                    <tr className="text-secondary-400 border-b border-secondary-700">
                      <th className="py-2 pr-4">Side</th>
                      <th className="py-2 pr-4">NeonSportz / Discord</th>
                      <th className="py-2 pr-4">In-game identity</th>
                      <th className="py-2">Twitch login</th>
                    </tr>
                  </thead>
                  <tbody>
                    {participantPreview.map((row) => (
                      <tr key={`${row.sideLabel}-${row.sourceIdentity}`} className="border-b border-secondary-800">
                        <td className="py-2 pr-4">{row.sideLabel}</td>
                        <td className="py-2 pr-4">{row.sourceIdentity}</td>
                        <td className="py-2 pr-4">{row.inGameIdentity}</td>
                        <td className="py-2">{row.twitchLogin}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {!result.dry_run && result.success && (
            <Link to={lscPath(`/leagues/${leagueId}/matches`)} className="btn-primary inline-block">
              View league schedules
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default CsvImportPanel;
